import { BaseDocument } from "./base";
import { createPartial, createPartialExcluding } from "./helpers/partial";


export enum FreelancerExperienceLevel {
    T0,
    T1,
    T2,
    T3,
    T4,
    T5,
}

export enum FreelancerStatus {
    ACTIVE,
    INACTIVE,
    PENDING,
    SUSPENDED,
    BANNED,
    SOFTDELETE,
    ARCHIVED,
    LOCKED,
}

export enum FreelancerGender {
    MALE = 0,
    FEMALE = 1,
    OTHER = 2,
    NOT_PROVIDED,
}

export enum FreelancerRole {
    OTHER = -1,

    // Art and Design
    ART_2D = 0,
    ART_3D = 1,
    ANIMATION = 2,
    ART_LEADERSHIP = 3,
    CHARACTER_DESIGN = 4,
    ENVIRONMENT_DESIGN = 5,
    GRAPHIC_DESIGN = 6,
    TECHNICAL_ART = 7,
    UI_UX_DESIGN = 8,


    // Content Creation
    CONTENT_STRATEGY = 50,
    DIGITAL_MEDIA = 51,
    INFLUENCER = 52,
    SOCIAL_MEDIA = 53,
    STREAMER = 54,

    // Game Review
    ANALYST = 100,
    FOCUS_GROUPS = 101,
    PLAY_TESTER = 102,
    QA_TESTER = 103,
    REVIEW_WRITER = 104,

    // Voice and Audio
    AUDIO_ENGINEERING = 150,
    MUSIC_COMPOSITION = 151,
    PRODUCTION = 152,
    SOUND_DESIGN = 153,
    TRANSLATION_AND_LOCALIZATION_AUDIO = 154,
    VOICE_OVER = 155,

    // Writing and Translation
    NARRATIVE_DESIGN = 200,
    STORYLINES = 201,
    SCENARIO_AND_QUEST = 202,
    TRANSLATION_AND_LOCALIZATION_WRITING = 203,

    // Marketing and Community
    MARKETING_AND_COMMUNITY = 251,

    // Mentoring and Coaching
    MENTORING_AND_COACHING = 300,
}

export enum RestrictedFreelancerRole {
    PROJECT_MANAGER = 0,
}

export interface FreelancerSkillExperience {
    skill: string,
    experience: FreelancerExperienceLevel,
}

export interface FreelancerToolExperience {
    tool: string,
    experience: FreelancerExperienceLevel,
}

export interface Freelancer extends BaseDocument {
    _techMetadata: {
        firebaseUid: string,
    } | null,
    _currentStatus: {
        status: FreelancerStatus,
        date: Date,
        descriptionHeader: string,
        detailedDescription: string,
    },
    _statusHistory: {
        status: FreelancerStatus,
        date: Date,
        descriptionHeader: string,
        detailedDescription: string,
    }[],
    _activityHistory: any[],
    _referralCode: string,
    _referredBy: string,
    _referredUsersApproved: string[],
    _referredUsersPending: string[],
    _payoutMethods: any[],
    _preferredPaymentMethod: string,
    _emailVerified: boolean,
    _emailVerfifiedAt: Date | null,
    _phoneVerified: boolean,
    _phoneVerifiedAt: Date | null,
    _preferences: {
        language: string,
        darkMode: boolean,
        emailNotifications: boolean,
        phoneNotifications: boolean,
    }
    _prestigeScore: number,
    _profileCompletedPercentage: number,
    _projectManagerIntentAt: Date | null,
    _projectManagerAt: Date | null,
    _projectManagerWithdrawnAt: Date | null,

    _usedCreatedRoadmap: number,

    _employerWhiteList: string[],
    _employerBlackList: string[],
    _freelancerWhiteList: string[],
    _freelancerBlackList: string[],

    _hideProfile: boolean,
    _minHourlyRate: number,
    _maxHourlyRate: number,
    _walletId: string,

    lastActivityAt: Date | null,
    lastLoginAt: Date | null,
    lastLogoutAt: Date | null,

    timezone: string | null,
    gender: FreelancerGender | null,
    phone: string,
    email: string,
    contactEmail: string,
    streetAddress: string,
    streetAddress2: string | null,
    city: string,
    state: string,
    zipCode: string,
    country: string,
    profilePictureUrl: string,
    titlePictureUrl: string,
    introductionVideoUrl: string,
    galleryPicturesUrls: string[],
    galleryVideosUrls: string[],
    firstName: string,
    middleName: string,
    lastName: string,
    birthDate: Date | null,
    bio: string,
    currentOccupation: string,
    socialLinks: {
        linkedin: string,
        facebook: string,
        x: string,
        instagram: string,
        github: string,
        youtube: string,
        stackoverflow: string,
        pinterest: string,
        tiktok: string,
        reddit: string,
        behance: string,
        dribbble: string,
        websites: string[],
    },

    fullLegalName: string,
    businessName: string | null,
    taxId: string,
    exemptPayeeCode: string | null,
    exemptionFromFatcaReportingCode: string | null,

    projectManagerIntent: boolean,
    pmlvl: number,

    roles: FreelancerRole[],
    restrictedRoles: RestrictedFreelancerRole[],

    skills: FreelancerSkillExperience[],
    tools: FreelancerToolExperience[],
    industries: string[],
    languages: string[],
    furtherInfo: string[],
    certifications: {
        name: string,
        issuingOrganization: string,
        issueDate: Date,
        expirationDate: Date,
        certificateId: string,
        certificateUrl: string,
        description: string,
        level: string,
        skills: string[],
    }[],
    highestDegree: string,
    highestDegreeInstitution: string,
    educationHistory: {
        degree: string,
        country: string,
        school: string,
        year: number,
        month: number,
    }[],
    awards: string[],
    workHistory: {
        place: string,
        role: string,
        country: string,
        usedSkills: string[],
        usedTools: string[],
        startDate: Date | null,
        endDate: Date | null,
    }[],
    projectHistory: {
        title: string,
        subtitle: string,
        country: string,
        description: string,
        usedSkills: string[],
        usedTools: string[],
    }[],
    portfolioLinks: {
        name: string,
        url: string
    }[],
    availabilityHoursPerWeek: number,
    availabilityTimeFlexible: boolean,
    workingAvailabilityPrecise: {
        dayOfWeek: string,
        date: Date | null,
        availabilityHours: number,
        flexibleInTime: boolean,
        startTime: Date | null,
        endTime: Date | null,
    }[]
    reviews: {
        createdAt: Date,
        reviewerId: string,
        reviewerType: string,
        totalRating: number,
        detailedRatings: {
            qualityOfWork: number,
            communication: number,
            timeliness: number,
            professionalism: number,
            technicalSkills: number,
            creativity: number,
            projectManagement: number,
        }
    }[],

    platformCurrentProjectManagements: {
        projectId: string,
        hoursPerWeek: number,
        assignedAt: Date,
        completedAt: Date | null,
    }[],
    platformCompletedProjectManagements: {
        projectId: string,
        hoursPerWeek: number,
        assignedAt: Date,
        completedAt: Date | null,
    }[],
    platformUnassignedProjectManagements: {
        projectId: string,
        reason: string | null,
        assignedAt: Date,
        unassignedAt: Date | null,
    }[],
    platformCurrentRoadmapVerifications: {
        projectId: string,
        assignedAt: Date,
        completedAt: Date | null,
    }[],
    platformUnassignedRoadmapVerifications: {
        projectId: string,
        reason: string | null,
        assignedAt: Date,
        unassignedAt: Date | null,
    }[],
    platformCompletedRoadmapVerifications: {
        projectId: string,
        assignedAt: Date,
        completedAt: Date | null,
    }[],
    platformCompletedJobs: {
        taskId: string,
        projectId: string,
        assignedAt: Date,
        completedAt: Date | null,
    }[],
    platformCurrentJobs: {
        taskId: string,
        projectId: string,
        assignedAt: Date,
        completedAt: Date | null,
    }[],
    platformUnassignedJobs: {
        reason: string | null,
        taskId: string,
        projectId: string | null,
        assignedAt: Date,
        unassignedAt: Date | null,
    }[],

}

export const createDefaultFreelancer = (): Freelancer => ({
    _techMetadata: null,
    _currentStatus: {
        status: FreelancerStatus.PENDING,
        date: new Date(),
        descriptionHeader: 'Signup initiated',
        detailedDescription: '',
    },
    _statusHistory: [{
        status: FreelancerStatus.PENDING,
        date: new Date(),
        descriptionHeader: 'Signup initiated',
        detailedDescription: '',
    }],
    _activityHistory: [],
    _referralCode: '',
    _referredBy: '',
    _referredUsersApproved: [],
    _referredUsersPending: [],
    _payoutMethods: [],
    _preferredPaymentMethod: '',
    _emailVerified: false,
    _emailVerfifiedAt: null,
    _phoneVerified: false,
    _phoneVerifiedAt: null,
    _preferences: {
        language: 'en',
        darkMode: false,
        emailNotifications: true,
        phoneNotifications: false,
    },
    _profileCompletedPercentage: 0,
    _prestigeScore: -1,
    _projectManagerIntentAt: null,
    _projectManagerAt: null,
    _projectManagerWithdrawnAt: null,

    _usedCreatedRoadmap: 0,

    _employerWhiteList: [],
    _employerBlackList: [],
    _freelancerWhiteList: [],
    _freelancerBlackList: [],

    _hideProfile: false,
    _minHourlyRate: -1,
    _maxHourlyRate: -1,
    _walletId: '',

    lastActivityAt: null,
    lastLoginAt: null,
    lastLogoutAt: null,
    timezone: null,
    gender: null,
    phone: '',
    email: '',
    contactEmail: '',
    streetAddress: '',
    streetAddress2: null,
    city: '',
    state: '',
    zipCode: '',
    country: '',
    profilePictureUrl: '',
    titlePictureUrl: '',
    introductionVideoUrl: '',
    galleryPicturesUrls: [],
    galleryVideosUrls: [],
    firstName: '',
    middleName: '',
    lastName: '',
    birthDate: null,
    bio: '',
    currentOccupation: '',
    socialLinks: {
        linkedin: '',
        facebook: '',
        x: '',
        instagram: '',
        github: '',
        youtube: '',
        stackoverflow: '',
        pinterest: '',
        tiktok: '',
        reddit: '',
        behance: '',
        dribbble: '',
        websites: [],
    },

    fullLegalName: '',
    businessName: null,
    taxId: '',
    exemptPayeeCode: null,
    exemptionFromFatcaReportingCode: null,

    projectManagerIntent: false,
    pmlvl: 0,

    roles: [],
    restrictedRoles: [],

    skills: [],
    tools: [],
    industries: [],
    languages: [],
    furtherInfo: [],
    certifications: [],
    highestDegree: '',
    highestDegreeInstitution: '',
    educationHistory: [],
    awards: [],
    workHistory: [],
    portfolioLinks: [],
    availabilityHoursPerWeek: 0,
    availabilityTimeFlexible: false,
    workingAvailabilityPrecise: [],
    reviews: [],
    projectHistory: [],

    platformCurrentProjectManagements: [],
    platformCompletedProjectManagements: [],
    platformUnassignedProjectManagements: [],
    platformCurrentRoadmapVerifications: [],
    platformUnassignedRoadmapVerifications: [],
    platformCompletedRoadmapVerifications: [],
    platformCompletedJobs: [],
    platformCurrentJobs: [],
    platformUnassignedJobs: [],


});

export function percentageOfProfileCompleted(freelancer: IFreelancer): number {
    // necessary fields
    // firstname, lastname, country, profile picture, bio, skills, tools, industries, languages, roles
    const necessaryFields = [
        'firstName',
        'lastName',
        'country',
        'profilePictureUrl',
        'bio',
        'skills',
        'tools',
        'industries',
        'languages',
        'roles',
    ]
    const numberOfNecessaryFields = necessaryFields.length - 1; // -1 because either skills or tools is ok
    let completedFields = 0;

    if (freelancer.firstName && freelancer.firstName.trim() !== '') { completedFields++; }
    if (freelancer.lastName && freelancer.lastName.trim() !== '') { completedFields++; }
    if (freelancer.country && freelancer.country.trim() !== '') { completedFields++; }
    if (freelancer.profilePictureUrl && freelancer.profilePictureUrl.trim() !== '') { completedFields++; }
    if (freelancer.bio && freelancer.bio.trim() !== '') { completedFields++; }
    if ((freelancer.skills && freelancer.skills.length > 0) ||
        (freelancer.tools && freelancer.tools.length > 0)) { completedFields++; }
    if (freelancer.industries && freelancer.industries.length > 0) { completedFields++; }
    if (freelancer.languages && freelancer.languages.length > 0) { completedFields++; }
    if (freelancer.roles && freelancer.roles.length > 0) { completedFields++; }

    const percentage = Math.round(((completedFields / numberOfNecessaryFields) * 100));
    return percentage;
}

export function missingFieldsForCompletion(freelancer: IFreelancer): string[] {
    const necessaryFields = [
        'firstName',
        'lastName',
        'country',
        'profilePictureUrl',
        'bio',
        'skills',
        'tools',
        'industries',
        'languages',
        'roles',
    ]
    const missingFields: string[] = [];
    if (!freelancer.firstName || freelancer.firstName.trim() === '') missingFields.push('Firstname');
    if (!freelancer.lastName || freelancer.lastName.trim() === '') missingFields.push('Lastname');
    if (!freelancer.country || freelancer.country.trim() === '') missingFields.push('Country');
    if (!freelancer.profilePictureUrl || freelancer.profilePictureUrl.trim() === '') missingFields.push('Profile picture');
    if (!freelancer.bio || freelancer.bio.trim() === '') missingFields.push('Bio');
    if (!((freelancer.skills && freelancer.skills.length > 0) ||
        (freelancer.tools && freelancer.tools.length > 0))) missingFields.push('Skills or Tools');
    if (!(freelancer.industries && freelancer.industries.length > 0)) missingFields.push('Industries');
    if (!(freelancer.languages && freelancer.languages.length > 0)) missingFields.push('Languages');
    if (!(freelancer.roles && freelancer.roles.length > 0)) missingFields.push('Roles');
    return missingFields;
}

export function full(freelancer: IFreelancer): IFreelancer {
    return freelancer
}

export function freelancerMeFull(freelancer: IFreelancer): IFreelancer {
    const excludeFields = [
        '_techMetadata',
    ]
    return createPartialExcluding(freelancer, excludeFields);
}

export function freelancerMeBrief(freelancer: IFreelancer): IFreelancer {
    const fields = [
        '_id',
        'firstName',
        'lastName',
        'profilePictureUrl',
        'titlePictureUrl',
        'bio',
        'reviewsTotal',
        'reviewsNumber',
    ]
    return createPartial(freelancer, fields);
}

export function freelancerFull(freelancer: IFreelancer): IFreelancer {
    return freelancer;
}

export function freelancerBrief(freelancer: IFreelancer): IFreelancer {
    const fields = [
        '_id',
        '_createdAt',
        '_updatedAt',
        'firstName',
        'middleName',
        'lastName',
        'profilePictureUrl',
        'country',
        'titlePictureUrl',
        'bio',
        'reviewsTotal',
        'reviewsNumber',
        'skills',
        'tools',
        'industries',
        'languages',
        'roles',
        'restrictedRoles'
    ]
    return createPartial(freelancer, fields);
}


export interface IFreelancer extends Partial<Freelancer> { };
